import React from 'react';
import { Typography, DatePicker, Select, Checkbox } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import { CustomTooltip } from './custom-tooltip';

const { Text } = Typography;

type WEEK_DAYS = 'Domingo' | 'Lunes' | 'Martes' | 'Miercoles' | 'Jueves' | 'Viernes' | 'Sabado';

interface CalendarFieldProps {
  field: any;
  value: string;
  onChange: (value: string) => void;
  commonStyle: React.CSSProperties;
  labelStyle: React.CSSProperties;
  error?: string;
  formData?: Record<string, any>;
  onFieldChange?: (fieldKey: string, value: any) => void;
}

// Mapeo de días en español (solo el día)
const DAY_MAPPING: Record<number, string> = {
  0: 'Domingo',
  1: 'Lunes',
  2: 'Martes',
  3: 'Miercoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sabado'
};

export const CalendarField: React.FC<CalendarFieldProps> = ({
  field,
  value,
  onChange,
  commonStyle,
  labelStyle,
  error,
  formData,
  onFieldChange
}) => {
  const today = dayjs();
  const [isLive, setIsLive] = React.useState(true); // Estado para controlar "en vivo"

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      const isoDate = date.format('YYYY-MM-DD');
      onChange(isoDate);

      // Auto-completar el campo day si existe la función onFieldChange
      if (onFieldChange && field.showLiveOption) {
        const dayOfWeek = date.day();
        const dayName = DAY_MAPPING[dayOfWeek];
        const finalDayValue = isLive ? `${dayName} - en vivo` : dayName;
        onFieldChange('day', finalDayValue);
      }
    }
  };

  const handleLiveChange = (checked: boolean) => {
    setIsLive(checked);

    // Actualizar el valor del día inmediatamente
    if (onFieldChange && value) {
      const date = dayjs(value, 'YYYY-MM-DD');
      const dayOfWeek = date.day();
      const dayName = DAY_MAPPING[dayOfWeek];
      const finalDayValue = checked ? `${dayName} - en vivo` : dayName;
      onFieldChange('day', finalDayValue);
    }
  };

  const dateValue = value ? dayjs(value, 'YYYY-MM-DD') : today;

  // Función para deshabilitar fechas anteriores a hoy
  const disabledDate = (current: Dayjs) => {
    return current && current.isBefore(today, 'day');
  };

  // Si este campo es para fecha y también hay un campo day en el form, mostrar ambos
  const showDayField = field.showLiveOption && formData && onFieldChange;
  const dayValue = formData?.['day'];

  // Opciones dinámicas según el estado de "en vivo"
  const getDayOptions = () => {
    const baseDays: WEEK_DAYS[] = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado'];
    return baseDays.map(day => ({
      value: isLive ? `${day} - en vivo` : day,
      label: isLive ? `${day} - en vivo` : day
    }));
  };

  React.useEffect(() => {
    // Si hay fecha y se debe auto-completar day
    if (value && onFieldChange && field.showLiveOption) {
      const date = dayjs(value, 'YYYY-MM-DD');
      const dayOfWeek = date.day();
      const dayName = DAY_MAPPING[dayOfWeek];
      const finalDayValue = isLive ? `${dayName} - en vivo` : dayName;

      // Solo actualizar si day no coincide (evitar loop infinito)
      if (formData?.['day'] !== finalDayValue) {
        onFieldChange('day', finalDayValue);
      }
    }
  }, [value, isLive, onFieldChange, field.showLiveOption, formData]);

  return (
    <div
      style={{
        ...commonStyle,
        flexDirection: 'row',
        marginBottom: 0,
        gap: 20,
        alignItems: 'center',
        width: '100%'
      }}
    >
      <div style={{ ...commonStyle, width: 150 }}>
        <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
          {field.label}
        </Text>
        <CustomTooltip field={field}>
          <DatePicker
            placeholder={field.placeholder || 'Seleccionar fecha'}
            style={{ width: '100%', fontSize: 12 }}
            value={dateValue}
            onChange={handleDateChange}
            format='YYYY-MM-DD'
            status={error ? 'error' : undefined}
            disabledDate={disabledDate}
            allowClear={false}
            inputReadOnly={true}
          />
        </CustomTooltip>
        {error && (
          <Text type='danger' style={{ fontSize: 12, marginTop: 4 }}>
            {error}
          </Text>
        )}
      </div>

      {/* Campo día autocompletado */}
      {showDayField && (
        <div style={{ ...commonStyle, width: 150 }}>
          <Text type='secondary' style={{ ...labelStyle, textTransform: 'uppercase' }}>
            Día
          </Text>
          <Select
            placeholder='Día de la semana'
            style={{ width: '100%', textAlign: 'left' }}
            options={getDayOptions()}
            value={dayValue}
            onChange={value => onFieldChange('day', value)}
            disabled={true} // Deshabilitado porque se autocompleta
          />
        </div>
      )}

      {/* Checkbox para "en vivo" */}
      {showDayField && (
        <div style={{ ...commonStyle, justifyContent: 'flex-end', paddingTop: 30, width: 150 }}>
          <Checkbox checked={isLive} onChange={e => handleLiveChange(e.target.checked)}>
            <Text style={{ fontSize: 12 }}>En vivo</Text>
          </Checkbox>
        </div>
      )}
    </div>
  );
};
